import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useAllPiecesInfinite } from '@/lib/queries/pieces';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { FormInput } from '@/components/molecules/form-input';
import { Button, buttonVariants } from '@/components/atoms/button';
import { Icon } from '@/components/atoms/icon';
import { Chip } from '@/components/atoms/chip';
import { Segmented } from '@/components/atoms/segmented';
import { Micro } from '@/components/atoms/micro';
import { MobileHeader } from '@/components/organisms/mobile-header';
import { LibraryPieceCard } from '@/components/molecules/library-piece-card';
import { CollectionCard, type CollectionTone } from '@/components/molecules/collection-card';
import { EmptyState } from '@/components/molecules/empty-state';
import { cn } from '@/lib/cn';

type SortValue = 'name-asc' | 'name-desc';

const SORT_OPTIONS: readonly { value: SortValue; label: string }[] = [
  { value: 'name-asc', label: 'Name ↓' },
  { value: 'name-desc', label: 'Name ↑' },
] as const;

const SORT_VALUES: readonly SortValue[] = SORT_OPTIONS.map((o) => o.value);
const DEFAULT_SORT: SortValue = 'name-asc';

// Default-valued params are omitted from the URL: validateSearch returns
// undefined for a param at its default so it does not serialize into the
// query string. The component supplies the default on read.
type LibrarySearch = {
  q?: string;
  sort?: SortValue;
  form?: string;
};

export const Route = createFileRoute('/library')({
  component: LibraryPage,
  validateSearch: (raw: Record<string, unknown>): LibrarySearch => ({
    q: typeof raw.q === 'string' && raw.q !== '' ? raw.q : undefined,
    sort:
      SORT_VALUES.includes(raw.sort as SortValue) && raw.sort !== DEFAULT_SORT
        ? (raw.sort as SortValue)
        : undefined,
    form: typeof raw.form === 'string' ? raw.form : undefined,
  }),
});

// Curated collections: no backend model yet — fixtures only.
const COLLECTIONS: readonly { name: string; pieceCount: number; toneKey: CollectionTone }[] = [
  { name: "O'Carolan core",   pieceCount: 32, toneKey: 'warm' },
  { name: 'Donegal reels',    pieceCount: 48, toneKey: 'brand' },
  { name: 'Session starters', pieceCount: 24, toneKey: 'ok' },
  { name: 'Breton dances',    pieceCount: 18, toneKey: 'neutral' },
] as const;

const ALL_FORMS_CHIP = 'All forms';

// Chip labels read naturally as plurals; the canonical form values on
// piece.forms are singular pretty-names ("Reel", "Jig"). This map joins the
// two. Chips not present here are treated like "All forms" (no filter).
const CHIP_TO_FORM: Readonly<Record<string, string>> = {
  Reels: 'Reel',
  Jigs: 'Jig',
  Airs: 'Air',
  Hornpipes: 'Hornpipe',
  'Set dances': 'Set dance',
  Waltzes: 'Waltz',
  Polkas: 'Polka',
  'Slip jigs': 'Slip Jig',
};

const FORM_TO_CHIP: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(CHIP_TO_FORM).map(([chip, form]) => [form, chip]),
);

const FORM_CHIPS: readonly string[] = [
  ALL_FORMS_CHIP, 'Reels', 'Jigs', 'Airs', 'Hornpipes', 'Set dances', 'Waltzes', 'Polkas', 'Slip jigs',
] as const;

const SEARCH_DEBOUNCE_MS = 300;

function LibraryPage(): ReactElement {
  const search = Route.useSearch();
  const q = search.q ?? '';
  const sort = search.sort ?? DEFAULT_SORT;
  const form = search.form;
  const navigate = useNavigate({ from: '/library' });

  // The input value is local for keystroke responsiveness; its debounced
  // value is written to the URL, which is the single source of truth the
  // query reads from.
  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const activeChip = form === undefined ? ALL_FORMS_CHIP : FORM_TO_CHIP[form] ?? ALL_FORMS_CHIP;

  useEffect(() => {
    if (debouncedSearch === q) return;
    void navigate({
      search: (prev) => ({ ...prev, q: debouncedSearch === '' ? undefined : debouncedSearch }),
      replace: true,
    });
  }, [debouncedSearch, q, navigate]);

  const handleSearch = (value: string): void => {
    setSearchInput(value);
  };

  const setSort = (next: SortValue): void => {
    void navigate({ search: (prev) => ({ ...prev, sort: next }) });
  };

  const selectChip = (chip: string): void => {
    const nextForm = chip === ALL_FORMS_CHIP ? undefined : CHIP_TO_FORM[chip];
    void navigate({ search: (prev) => ({ ...prev, form: nextForm }) });
  };

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAllPiecesInfinite(q);

  const loadedPieces = useMemo(
    () => data?.pages.flatMap((page) => page.content) ?? [],
    [data],
  );

  const pieces = useMemo(() => {
    const arr = [...loadedPieces];
    arr.sort((a, b) => a.prettyName.localeCompare(b.prettyName));
    if (sort === 'name-desc') arr.reverse();
    if (form === undefined) return arr;
    return arr.filter((p) => (p.forms ?? []).includes(form));
  }, [loadedPieces, sort, form]);

  const total = data?.pages.at(-1)?.totalElements ?? 0;
  const hasSearch = q.length > 0;
  const hasActiveFormFilter = form !== undefined;

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--ink)] flex flex-col">
      {/* Mobile header — hidden at md+ */}
      <div className="md:hidden">
        <MobileHeader
          large
          title="Library"
          subtitle={`${total.toLocaleString()} pieces`}
          trailing={
            <Link
              to="/library/contribute"
              aria-label="Contribute piece"
              className="inline-flex items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--bg-elevated)]"
              style={{ width: 36, height: 36 }}
            >
              <Icon name="plus" size={18} />
            </Link>
          }
        />
      </div>

      {/* Tablet + desktop top bar — hidden below md */}
      <div className="hidden md:flex h-16 px-8 items-center border-b border-[var(--rule)] bg-[var(--bg-surface)]">
        <div className="flex-1">
          <Micro className="block mb-0.5">Library</Micro>
          <div className="font-display text-[20px] font-semibold tracking-tight">
            {total.toLocaleString()} pieces
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/library/contribute"
            className={cn(buttonVariants({ variant: 'primary', size: 'md' }))}
          >
            <Icon name="plus" size={15} />
            Contribute piece
          </Link>
        </div>
      </div>

      {/* Search + chips + sort */}
      <div className="px-5 md:px-8 pt-4 md:pt-6 bg-[var(--bg-surface)] md:bg-transparent">
        <FormInput
          label="Search pieces"
          icon="search"
          placeholder={`Search ${total.toLocaleString()} pieces…`}
          value={searchInput}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className="flex items-center gap-1.5 mt-3 overflow-x-auto pb-1">
          {FORM_CHIPS.map((c) => (
            <Chip key={c} active={c === activeChip} onClick={() => selectChip(c)}>{c}</Chip>
          ))}
        </div>
        <div className="hidden md:flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
            <span className="font-semibold">{pieces.length} showing</span>
            {hasSearch && <span>· filtered by &ldquo;{q}&rdquo;</span>}
          </div>
          <Segmented size="sm" value={sort} onChange={(v) => setSort(v)} options={[...SORT_OPTIONS]} />
        </div>
      </div>

      {/* Curated collections — horizontal scroll on mobile, 2-col tablet, 4-col desktop */}
      <section className="px-5 md:px-8 pt-5">
        <Micro className="block mb-3">Collections · curated</Micro>
        <div className="flex gap-2 overflow-x-auto md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-3 md:overflow-visible">
          {COLLECTIONS.map((c) => (
            <div key={c.name} className="min-w-[160px] md:min-w-0">
              <CollectionCard {...c} />
            </div>
          ))}
        </div>
      </section>

      {/* Pieces grid — 1 / 2 / 3 columns */}
      <section className="px-5 md:px-8 py-6 flex-1">
        <div className="flex items-center justify-between mb-3">
          <Micro>All pieces · A–Z</Micro>
        </div>

        {isLoading && (
          <div className="text-[12px] text-[var(--ink-3)]">Loading…</div>
        )}

        {!isLoading && pieces.length === 0 && (
          <EmptyState
            title={
              hasSearch || hasActiveFormFilter
                ? 'No pieces match your filter.'
                : 'Your catalog is empty.'
            }
            description={
              hasSearch || hasActiveFormFilter
                ? 'Try a different name or form.'
                : 'Contribute the first piece to the catalog.'
            }
            action={
              <Link
                to="/library/contribute"
                className={cn(buttonVariants({ variant: 'primary', size: 'md' }))}
              >
                <Icon name="plus" size={15} />
                Contribute piece
              </Link>
            }
          />
        )}

        {!isLoading && pieces.length > 0 && (
          <div
            data-library-grid
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {pieces.map((p) => (
              <Link
                key={p.pieceId}
                to="/library/$pieceId"
                params={{ pieceId: p.pieceId }}
                className="block"
              >
                <LibraryPieceCard
                  title={p.prettyName}
                  composer={p.composerName ?? 'Traditional'}
                  forms={p.forms ?? []}
                />
              </Link>
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="secondary"
              size="md"
              disabled={isFetchingNextPage}
              aria-busy={isFetchingNextPage || undefined}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? 'Loading more…' : 'Load more'}
            </Button>
          </div>
        )}
      </section>

    </div>
  );
}
